<script setup lang="ts">
import type { Company } from "@asap/shared/schemas/company";
import { useCompanyApi } from "~/components/company/composables/useCompanyApi";
import { useToast } from "~/composables/useToast";

const props = defineProps<{
  company: Company;
}>();

const emit = defineEmits<{
  onUpdate: [company: Company];
}>();

const { t } = useI18n();
const { updateCompanyRisk } = useCompanyApi();
const { addToastError, addToastSuccess } = useToast();

const riskReason = ref<string | undefined>(undefined);
const isUpdating = ref(false);

onMounted(() => {
  riskReason.value = props.company.risk_reason ?? undefined;
});

const updateRisk = async (close: () => void) => {
  try {
    isUpdating.value = true;
    const updatedCompany = await updateCompanyRisk(props.company.id, {
      risk_level: "high",
      risk_reason: riskReason.value || "",
    });
    close();
    riskReason.value = undefined;
    addToastSuccess({ description: t("companies.updateRiskModal.toast.updated", { name: props.company.name }) });
    emit("onUpdate", updatedCompany);
  } catch (error) {
    addToastError(
      { title: t("companies.updateRiskModal.toast.updateError"), description: t("companies.toast.contactSupport") },
      error
    );
  } finally {
    isUpdating.value = false;
  }
};

const ui = {
  title: "flex flex-row items-center gap-2 text-secondary-900 font-size-4",
  titleIcon: "font-size-4",
  titleText: "text-md flex flex-row items-center gap-1",
  companyName: "max-w-50",
  body: "w-[97%] pl-[2.5%] my-5",
  fields: "flex flex-col gap-4 mt-4",
  field: "w-full",
  footer: "flex justify-end w-full gap-2",
};
</script>

<template>
  <AwModal position="center" size="md">
    <template #default>
      <AwButton size="md" color="critical" :is-loading="isUpdating">
        <AwIcon name="i-ph-warning-diamond-fill" class="size-3.5" />
        {{ $t("companies.updateRiskModal.trigger") }}
      </AwButton>
    </template>
    <template #title>
      <div :class="ui.title">
        <AwIcon name="i-ph-receipt-x-duotone" :class="ui.titleIcon" />
        <i18n-t keypath="companies.updateRiskModal.title" tag="div" scope="global" :class="ui.titleText">
          <template #company>
            <div :class="ui.companyName">
              <TextEllipsis>{{ company.name }}</TextEllipsis>
            </div>
          </template>
        </i18n-t>
      </div>
    </template>
    <template #body>
      <div :class="ui.body">
        <Banner
          :title="$t('companies.updateRiskModal.banner.title')"
          :description="$t('companies.updateRiskModal.banner.description')"
          asset="images/toolbox.svg"
          asset-size="sm"
        />

        <div :class="ui.fields">
          <AwFormField :label="$t('companies.updateRiskModal.reason.label')" :class="ui.field" required>
            <AwTextarea
              :class="ui.field"
              :placeholder="$t('companies.updateRiskModal.reason.placeholder')"
              :model-value="riskReason"
              @update:model-value="riskReason = $event"
            />
          </AwFormField>
        </div>
      </div>
    </template>
    <template #footer="{ close }">
      <div :class="ui.footer">
        <AwButton color="secondary" variant="ghost" @click="close()">{{ $t("common.actions.cancel") }}</AwButton>
        <AwButton
          color="secondary"
          :disabled="riskReason === undefined || riskReason.length === 0"
          :is-loading="isUpdating"
          @click="() => updateRisk(close)"
        >
          {{ $t("companies.updateRiskModal.confirm") }}
        </AwButton>
      </div>
    </template>
  </AwModal>
</template>
